import React from 'react';
import { Marker, Polygon, Circle, Tooltip } from 'react-leaflet';
import { IconHazard } from '../../Icons/IconHazard';
import { getSeverityColor } from '../../../../../constants/hazardStatus';
import { HAZARD_AREA_STYLE } from './hazardUtils';

/**
 * Renders a single hazard as the appropriate Leaflet shape
 * (Polygon, Circle, or Marker) based on hazard.shape_type.
 *
 * Shared by Hazard.js (editable) and ActivityHazardMarkers.js (read-only).
 */
const HazardShape = ({ hazard, keyPrefix, onClick, onEdit, onDragEnd, onRemove, riseOnHover }) => {
  const shapeType = hazard.shape_type || 'marker';
  const elementKey = keyPrefix ? `${keyPrefix}-${hazard.id}` : hazard.id;

  switch (shapeType) {
    case 'polygon':
    case 'rectangle': {
      if (!hazard.area || hazard.area.length === 0) return null;
      const positions = hazard.area.map(coord => {
        if (typeof coord === 'string') {
          const [lat, lng] = coord.split(',').map(v => parseFloat(v.trim()));
          return [lat, lng];
        }
        return [parseFloat(coord[0]), parseFloat(coord[1])];
      }).filter(pos => !isNaN(pos[0]) && !isNaN(pos[1]));
      const severityColor = getSeverityColor(hazard.severity);
      return (
        <Polygon
          key={elementKey}
          positions={positions}
          color={severityColor}
          fillColor={severityColor}
          {...HAZARD_AREA_STYLE}
          onClick={onClick}
          onEdit={onEdit}
          onRemove={onRemove}
        >
          <Tooltip permanent direction="center" className="hazard-area-tooltip">
            {hazard.title || 'Hazard'}
          </Tooltip>
        </Polygon>
      );
    }
    case 'circle': {
      if (!hazard.circle_lat || !hazard.circle_lng || !hazard.circle_radius) return null;
      const severityColor = getSeverityColor(hazard.severity);
      return (
        <Circle
          key={elementKey}
          center={[parseFloat(hazard.circle_lat), parseFloat(hazard.circle_lng)]}
          radius={parseFloat(hazard.circle_radius)}
          color={severityColor}
          fillColor={severityColor}
          {...HAZARD_AREA_STYLE}
          onClick={onClick}
          onEdit={onEdit}
          onRemove={onRemove}
        >
          <Tooltip permanent direction="center" className="hazard-area-tooltip">
            {hazard.title || 'Hazard'}
          </Tooltip>
        </Circle>
      );
    }
    default: {
      if (!hazard.lat || !hazard.lng || !hazard.id) return null;
      return (
        <Marker
          key={elementKey}
          position={[parseFloat(hazard.lat), parseFloat(hazard.lng)]}
          icon={IconHazard(hazard.severity || 'medium')}
          onClick={onClick}
          onDragEnd={onDragEnd}
          onRemove={onRemove}
          riseOnHover={riseOnHover || false}
        />
      );
    }
  }
};

export default HazardShape;
